import * as React from "react";
import { useMemo, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { ConnectorCategory } from "@axio/graphql/generated/graphql";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSearchInput from "../../../common/general/CustomSearchInput/CustomSearchInput";
import { ConnectorCard } from "../ConnectorCard/ConnectorCard";
import {
  CONNECTOR_CATEGORY_LABELS,
  CONNECTOR_CATEGORY_ORDER,
  groupConnectorsByCategory,
  type CatalogConnector,
  type ConnectorApprovalSummary,
  type InstitutionalConnectionSummary,
} from "../catalog.utils";

interface CatalogTabProps {
  catalog: CatalogConnector[];
  loading: boolean;
  errorMessage: string | null;
  connectionMap?: Map<string, InstitutionalConnectionSummary>;
  approvalMap?: Map<string, ConnectorApprovalSummary>;
  onSelect: (id: string) => void;
}

const EMPTY_CONNECTION_MAP = new Map<string, InstitutionalConnectionSummary>();
const EMPTY_APPROVAL_MAP = new Map<string, ConnectorApprovalSummary>();

export function CatalogTab({
  catalog,
  loading,
  errorMessage,
  connectionMap = EMPTY_CONNECTION_MAP,
  approvalMap = EMPTY_APPROVAL_MAP,
  onSelect,
}: CatalogTabProps) {
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<ConnectorCategory | "all">("all");

  const filteredCatalog = useMemo(() => {
    let scopedCatalog = catalog;

    if (selectedCategory !== "all") {
      scopedCatalog = scopedCatalog.filter((connector) => connector.category === selectedCategory);
    }

    const trimmedSearch = search.trim().toLowerCase();

    if (!trimmedSearch) {
      return scopedCatalog;
    }

    return scopedCatalog.filter((connector) => {
      const categoryLabel = CONNECTOR_CATEGORY_LABELS[connector.category].toLowerCase();

      return (
        connector.name!.toLowerCase().includes(trimmedSearch) ||
        connector.description!.toLowerCase().includes(trimmedSearch) ||
        categoryLabel.includes(trimmedSearch)
      );
    });
  }, [catalog, search, selectedCategory]);

  const groupedCatalog = useMemo(() => {
    if (selectedCategory !== "all") {
      return filteredCatalog.length > 0 ? [{ category: selectedCategory, connectors: filteredCatalog }] : [];
    }

    return groupConnectorsByCategory(filteredCatalog);
  }, [filteredCatalog, selectedCategory]);

  return (
    <View style={styles.container}>
      <View style={styles.filtersRow}>
        <View style={styles.searchWrapper}>
          <CustomSearchInput minWidth={224} placeholder="Search..." searchQuery={search} setSearchQuery={setSearch} />
        </View>
        <View style={styles.divider} />
        <CategoryPill label="All" active={selectedCategory === "all"} onPress={() => setSelectedCategory("all")} />
        {CONNECTOR_CATEGORY_ORDER.map((category) => (
          <CategoryPill
            key={category}
            label={CONNECTOR_CATEGORY_LABELS[category]}
            active={selectedCategory === category}
            onPress={() => setSelectedCategory(category)}
          />
        ))}
      </View>

      {loading ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>Loading connectors...</Text>
        </View>
      ) : errorMessage ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>{errorMessage}</Text>
        </View>
      ) : groupedCatalog.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No connectors match your search.</Text>
        </View>
      ) : (
        <View style={styles.groups}>
          {groupedCatalog.map(({ category, connectors }) => (
            <View key={category} style={styles.group}>
              <View style={styles.sectionHeader}>
                <CustomText
                  text={CONNECTOR_CATEGORY_LABELS[category]}
                  size="s"
                  useTranslationText={false}
                  textType="text"
                  weight="bold"
                  role="heading"
                  aria-level="2"
                  style={styles.sectionTitle}
                />
                <CustomText
                  text={`(${connectors.length})`}
                  size="s"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="text"
                  style={styles.sectionCount}
                />
              </View>
              <View style={styles.cardGrid}>
                {connectors.map((connector) => {
                  const connection = connectionMap.get(connector.id);
                  const approval = approvalMap.get(connector.id);
                  const connectionStatus = approval?.status ?? connection?.status ?? null;
                  return (
                    <ConnectorCard
                      key={connector.id}
                      connector={connector}
                      connectionStatus={connectionStatus}
                      isApproved={approval?.status === "approved"}
                      onClick={onSelect}
                      style={styles.card}
                    />
                  );
                })}
              </View>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function CategoryPill({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="tab"
      accessibilityState={{ selected: active }}
      style={({ pressed }) => [
        styles.pill,
        active ? styles.pillActive : styles.pillInactive,
        pressed && styles.pillPressed,
      ]}>
      <CustomText
        text={label}
        size="s"
        useTranslationText={false}
        textType="text"
        weight="medium"
        role="text"
        style={[styles.pillText, active ? styles.pillTextActive : styles.pillTextInactive]}
      />
    </Pressable>
  );
}

const styles = StyleSheet.create({
  container: {},
  filtersRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  searchWrapper: {
    width: 224,
  },
  divider: {
    width: 1,
    height: 28,
    backgroundColor: "#EAECF0",
  },
  pill: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 12,
  },
  pillActive: {
    backgroundColor: "#1D4ED8",
  },
  pillInactive: {
    backgroundColor: "#F3F4F6",
  },
  pillPressed: {
    opacity: 0.75,
  },
  pillText: {
    fontSize: 13,
    fontWeight: "500",
  },
  pillTextActive: {
    color: "#FFFFFF",
  },
  pillTextInactive: {
    color: "#6B7280",
  },
  emptyState: {
    paddingVertical: 48,
    alignItems: "center",
  },
  emptyText: {
    fontSize: 13,
    color: "#6B7280",
  },
  groups: {
    gap: 32,
  },
  group: {
    gap: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    color: "#101828",
  },
  sectionCount: {
    color: "#667085",
  },
  cardGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
    width: "100%",
  },
  card: {
    width: "32%",
  },
});
